import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError } from '../../api/admin-client.js';
import {
  adjustAdminStock,
  createAdminWarehouse,
  fetchAdminBalances,
  fetchAdminMovements,
  fetchAdminWarehouses,
  updateAdminWarehouse,
  type CreateWarehouseInput,
  type UpdateWarehouseInput,
} from '../api/inventory-api.js';
import type { AdjustStockInput, BalancesListParams, MovementsListParams } from '../types/inventory.js';

async function requireToken(getToken: () => Promise<string | null>): Promise<string> {
  const token = await getToken();
  if (!token) {
    throw new AdminApiError('AUTHENTICATION_ERROR', 'Your session has expired. Please sign in again.', 401);
  }
  return token;
}

export function useAdminBalances(params: BalancesListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ['admin', 'inventory', 'balances', params],
    queryFn: async ({ signal }) => fetchAdminBalances(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 10_000,
  });
}

export function useAdminMovements(params: MovementsListParams) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ['admin', 'inventory', 'movements', params],
    queryFn: async ({ signal }) => fetchAdminMovements(params, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 10_000,
  });
}

export function useAdjustStock() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: AdjustStockInput) =>
      adjustAdminStock(input, { token: await requireToken(getToken) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'inventory'] });
    },
  });
}

export function useAdminWarehouses(params: { isActive?: boolean } = {}) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  return useQuery({
    queryKey: ['admin', 'inventory', 'warehouses', params],
    queryFn: async ({ signal }) =>
      fetchAdminWarehouses({ ...params, limit: 100, offset: 0 }, { token: await requireToken(getToken), signal }),
    enabled: isLoaded && isSignedIn === true,
    staleTime: 30_000,
  });
}

export function useAdminWarehouseMutations() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'inventory', 'warehouses'] });

  const create = useMutation({
    mutationFn: async (input: CreateWarehouseInput) =>
      createAdminWarehouse(input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateWarehouseInput }) =>
      updateAdminWarehouse(id, input, { token: await requireToken(getToken) }),
    onSuccess: invalidate,
  });

  return { create, update };
}
